import React, { FC, ReactNode } from "react";
import { Provider } from "react-redux";
import { ChakraProvider, CSSReset } from "@chakra-ui/react";
import { ErrorBoundary } from "@sentry/react";
import { AppErrorFallback } from "~/components";
import { store } from "~/store/store";
import { customTheme } from "./theme";

interface Props {
  children: ReactNode;
}

const { VITE_ENVIRONMENT } = import.meta.env;

// The ErrorBoundary lives inside ChakraProvider so its fallback renders with
// the theme, and because every route/effect error we care about originates
// below here (the provider setup itself does not throw in practice). Without a
// fallback a caught error blanks the whole app; AppErrorFallback gives users a
// recovery surface and a path back into the current route.
export const AppProvider: FC<Props> = ({ children }) => (
  <Provider store={store}>
    <React.StrictMode>
      <ChakraProvider theme={customTheme}>
        <CSSReset />
        <ErrorBoundary
          fallback={({ resetError }) => (
            <AppErrorFallback resetError={resetError} />
          )}
          showDialog={VITE_ENVIRONMENT !== "development"}
        >
          {children}
        </ErrorBoundary>
      </ChakraProvider>
    </React.StrictMode>
  </Provider>
);
